import { create } from 'zustand'
import { devtools } from 'zustand/middleware'
import { authService } from '@/services/auth/auth-service'
import type { AuthSession, RegisterData, PasswordResetData } from '@/services/auth/auth-service'
import type { User, Subscription, TickerHistoryEntry } from '@/types/schemas/api'
import { setIdbUserScope } from '@/workspace/idb-cache'
import { storageScopeId } from '@/services/auth/types'
import { clearPostLoginPath } from '@/storage/post-login-redirect'

const MAX_TICKER_HISTORY = 100

interface CartState {
  period: 'month' | 'year'
  promotionCode: string
  partnerCode: string
}

interface AccountState {
  user: User | null
  session: AuthSession | null
  isAuthenticating: boolean
  authError: string | null
  cart: CartState
  passwordResetSent: boolean
  loginMessage: string | null
  /** 'warning' for a forced sign-out's reason; 'success' for confirmations. */
  loginMessageTone: 'success' | 'warning'

  // Async actions
  login: (email: string, password: string) => Promise<AuthSession>
  register: (data: RegisterData) => Promise<void>
  confirmRegistration: (email: string, code: string) => Promise<void>
  logout: () => Promise<void>
  restoreSession: () => Promise<AuthSession | null>
  refreshSession: () => Promise<boolean>
  sendPasswordResetCode: (email: string) => Promise<void>
  submitPasswordReset: (data: PasswordResetData) => Promise<void>

  // Sync actions
  setUser: (user: User) => void
  updateUserProfile: (partial: Partial<User>) => void
  updateSubscriptions: (subs: Subscription[]) => void
  setTickerHistory: (entries: TickerHistoryEntry[]) => void
  addTickerHistory: (entry: TickerHistoryEntry) => void
  removeTickerHistory: (symbol: string) => void
  updateCart: (partial: Partial<CartState>) => void
  setLoginMessage: (message: string | null, tone?: 'success' | 'warning') => void
  clearAuthError: () => void
}

export const useAccountStore = create<AccountState>()(
  devtools(
    (set, get) => ({
      user: null,
      session: null,
      isAuthenticating: false,
      authError: null,
      cart: { period: 'month', promotionCode: '', partnerCode: '' },
      passwordResetSent: false,
      loginMessage: null,
      loginMessageTone: 'success',

      login: async (email, password) => {
        set({ isAuthenticating: true, authError: null }, undefined, 'login/pending')
        try {
          const session = await authService.login(email, password)
          if (session?.user?.sub) setIdbUserScope(storageScopeId(session.user))
          set({ isAuthenticating: false, session, loginMessage: null }, undefined, 'login/success')
          return session
        } catch (err) {
          set(
            { isAuthenticating: false, authError: (err as Error).message ?? 'Login failed' },
            undefined,
            'login/error',
          )
          throw err
        }
      },

      register: async (data) => {
        set({ isAuthenticating: true, authError: null }, undefined, 'register/pending')
        try {
          await authService.register(data)
          set({ isAuthenticating: false }, undefined, 'register/success')
        } catch (err) {
          set(
            { isAuthenticating: false, authError: (err as Error).message ?? 'Registration failed' },
            undefined,
            'register/error',
          )
          throw err
        }
      },

      confirmRegistration: async (email, code) => {
        await authService.confirmRegistration(email, code)
        const session = await authService.getSession()
        if (session) {
          if (session.user?.sub) setIdbUserScope(storageScopeId(session.user))
          set({ session }, undefined, 'confirmRegistration')
        }
      },

      logout: async () => {
        clearPostLoginPath()
        await authService.logout()
        setIdbUserScope('anonymous')
        set({ user: null, session: null, authError: null }, undefined, 'logout')
      },

      restoreSession: async () => {
        set({ isAuthenticating: true }, undefined, 'restoreSession/pending')
        try {
          const session = await authService.getSession()
          if (session?.user?.sub) setIdbUserScope(storageScopeId(session.user))
          set({ isAuthenticating: false, session }, undefined, 'restoreSession/success')
          return session
        } catch {
          set({ isAuthenticating: false, session: null }, undefined, 'restoreSession/error')
          return null
        }
      },

      refreshSession: async () => {
        // The single refresh entry point. Force-refreshes the tokens via the auth
        // service AND updates the reactive session here, so no caller can leave the
        // store stale (the drift that made a migrated user re-provision every reload).
        // On failure we leave the session in place — the base-query 401 path owns
        // logout, so a transient refresh miss shouldn't bounce the user.
        const session = await authService.refreshSession()
        if (session) set({ session }, undefined, 'refreshSession')
        return !!session
      },

      sendPasswordResetCode: async (email) => {
        await authService.sendPasswordResetCode(email)
        set({ passwordResetSent: true }, undefined, 'sendPasswordResetCode')
      },

      submitPasswordReset: async (data) => {
        await authService.submitPasswordReset(data)
        set(
          {
            passwordResetSent: false,
            loginMessage: 'Password reset successful. Please log in.',
            loginMessageTone: 'success',
          },
          undefined,
          'submitPasswordReset',
        )
      },

      setUser: (user) => set({ user }, undefined, 'setUser'),

      updateUserProfile: (partial) => {
        const { user } = get()
        if (user) set({ user: { ...user, ...partial } }, undefined, 'updateUserProfile')
      },

      updateSubscriptions: (subs) => {
        const { user } = get()
        if (user) set({ user: { ...user, subscriptions: subs } }, undefined, 'updateSubscriptions')
      },

      setTickerHistory: (entries) => {
        const { user } = get()
        if (user) set({ user: { ...user, tickerHistory: entries } }, undefined, 'setTickerHistory')
      },

      addTickerHistory: (entry) => {
        const { user } = get()
        if (user) {
          set(
            {
              user: {
                ...user,
                tickerHistory: [
                  entry,
                  ...user.tickerHistory.filter((t) => t.symbol !== entry.symbol),
                ].slice(0, MAX_TICKER_HISTORY),
              },
            },
            undefined,
            'addTickerHistory',
          )
        }
      },

      removeTickerHistory: (symbol) => {
        const { user } = get()
        if (user) {
          set(
            {
              user: {
                ...user,
                tickerHistory: user.tickerHistory.filter((t) => t.symbol !== symbol),
              },
            },
            undefined,
            'removeTickerHistory',
          )
        }
      },

      updateCart: (partial) =>
        set((s) => ({ cart: { ...s.cart, ...partial } }), undefined, 'updateCart'),
      setLoginMessage: (message, tone = 'success') =>
        set({ loginMessage: message, loginMessageTone: tone }, undefined, 'setLoginMessage'),
      clearAuthError: () => set({ authError: null }, undefined, 'clearAuthError'),
    }),
    { name: 'zustand/account', store: 'account', enabled: import.meta.env.DEV },
  ),
)
